import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, productImage } from "../lib/brand";
import { useAuth, authHeaders } from "../lib/auth";
import { Download, ArrowRight, Mail } from "lucide-react";
import { toast } from "sonner";

/**
 * Orders page supports two flows:
 *  1. Logged-in user → fetches /me/orders
 *  2. Guest with ?session=...&email=... query → looks up that single order via /checkout/status
 */
export default function Orders() {
  const { user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const sessionParam = params.get("session");
  const emailParam = params.get("email");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guestForm, setGuestForm] = useState({ session: sessionParam || "", email: emailParam || "" });

  const loadUserOrders = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/me/orders`, { headers: authHeaders() });
      setOrders(r.data || []);
    } catch (e) {
      toast.error("Could not load orders");
    } finally {
      setLoading(false);
    }
  };

  const loadGuestOrder = async () => {
    if (!sessionParam) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/checkout/status/${sessionParam}`);
      if (r.data.payment_status === "paid") {
        setOrders([{
          session_id: sessionParam,
          customer_email: r.data.email,
          amount: r.data.amount,
          products: r.data.products || [],
          payment_status: "paid",
        }]);
      } else {
        setOrders([]);
      }
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (user) loadUserOrders();
    else loadGuestOrder();
    // eslint-disable-next-line
  }, [user, authLoading, sessionParam]);

  const downloadFile = async (sessionId, product, email) => {
    try {
      const res = await axios.get(`${API}/downloads/${sessionId}/${product.id}`, {
        params: { email },
        responseType: "blob",
      });
      // If backend returned JSON with redirect_url, handle
      if (res.headers["content-type"]?.includes("application/json")) {
        const text = await res.data.text();
        const json = JSON.parse(text);
        if (json.redirect_url) {
          window.open(json.redirect_url, "_blank");
          return;
        }
      }
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ext = product.audio_path?.split(".").pop() || product.cover_path?.split(".").pop() || "jpg";
      a.download = `${product.title}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  // Guest lookup form
  if (!user && !sessionParam) {
    return (
      <div className="pt-32 pb-24 max-w-2xl mx-auto px-6 lg:px-12">
        <div className="text-[11px] tracking-[0.4em] uppercase text-[#279153] mb-3">Order lookup</div>
        <h1 className="font-serif-display text-4xl text-white mb-8">Find your order</h1>
        <p className="text-[#95A89C] mb-6">
          Logged in? Your orders appear here automatically. Bought as a guest? Use the link in your thank-you email,
          or paste your session ID & email below.
        </p>
        <form
          data-testid="guest-order-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!guestForm.session || !guestForm.email) return;
            window.location.search = `?session=${encodeURIComponent(guestForm.session)}&email=${encodeURIComponent(guestForm.email)}`;
          }}
          className="space-y-4 p-6 bg-[#09120C] border border-[#1A2B20]"
        >
          <input
            data-testid="guest-session-input"
            placeholder="Stripe session ID"
            value={guestForm.session}
            onChange={(e) => setGuestForm({ ...guestForm, session: e.target.value })}
            className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm"
          />
          <input
            data-testid="guest-email-input"
            type="email"
            placeholder="Email used at checkout"
            value={guestForm.email}
            onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })}
            className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm"
          />
          <button type="submit" data-testid="guest-lookup-submit" className="w-full px-6 py-3 bg-[#1E7040] hover:bg-[#165932] text-white text-xs tracking-[0.3em] uppercase">
            Find order
          </button>
        </form>
        <div className="mt-8 p-6 bg-[#09120C] border border-dashed border-[#1A2B20] text-center">
          <p className="text-sm text-[#95A89C] mb-4">Or sign in to see all your orders.</p>
          <Link to="/login" className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#279153] text-[#279153] text-xs tracking-[0.3em] uppercase hover:bg-[#279153] hover:text-white">
            Sign in <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  if (authLoading || loading) {
    return <div className="pt-40 px-6 text-[#95A89C]">Loading…</div>;
  }

  return (
    <div data-testid="orders-page" className="pt-32 pb-24 max-w-5xl mx-auto px-6 lg:px-12">
      <div className="border-b border-[#1A2B20] pb-8 mb-10">
        <div className="text-[11px] tracking-[0.4em] uppercase text-[#279153] mb-3">Your collection</div>
        <h1 className="font-serif-display text-5xl text-white">My orders</h1>
        {user && <p className="text-sm text-[#95A89C] mt-2">Signed in as {user.email}</p>}
      </div>

      {orders.length === 0 ? (
        <div data-testid="orders-empty" className="border border-dashed border-[#1A2B20] p-16 text-center">
          <Mail className="w-10 h-10 text-[#5C7064] mx-auto mb-4" />
          <p className="text-[#95A89C] mb-6">No paid orders yet.</p>
          <Link to="/music" className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E7040] hover:bg-[#165932] text-white text-xs tracking-[0.3em] uppercase">
            Browse the collection <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map((o) => (
            <div key={o.session_id} data-testid={`order-${o.session_id}`} className="p-6 bg-[#09120C] border border-[#1A2B20]">
              <div className="flex items-start justify-between mb-5 pb-5 border-b border-[#1A2B20]">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C7064]">Order</div>
                  <div className="text-xs text-[#95A89C] font-mono">{o.session_id.slice(0, 24)}…</div>
                </div>
                <div className="text-right">
                  <div className="font-serif-display text-2xl text-white">${Number(o.amount).toFixed(2)}</div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#279153]">Paid</div>
                </div>
              </div>
              <div className="space-y-3">
                {(o.products || []).map((p) => (
                  <div key={p.id} data-testid={`order-product-${p.id}`} className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-black flex-shrink-0 overflow-hidden">
                      <img src={productImage(p)} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C7064]">{p.type}</div>
                      <div className="font-serif-display text-lg text-white truncate">{p.title}</div>
                    </div>
                    <button
                      data-testid={`download-${p.id}`}
                      onClick={() => downloadFile(o.session_id, p, o.customer_email)}
                      className="flex items-center gap-2 px-4 py-2 border border-[#279153] text-[#279153] text-xs tracking-[0.3em] uppercase hover:bg-[#279153] hover:text-white"
                    >
                      <Download className="w-4 h-4" /> Download
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-5 border-t border-[#1A2B20] text-[10px] tracking-[0.3em] uppercase text-[#5C7064]">
                Un-watermarked, high-resolution files
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
